import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ShieldCheck } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { useMembers } from '../lib/members';
import { Chips, IconChip, EmptyState, ProgressBar, SkeletonList } from '../components/ui';
import { euro, timeLeftBadge } from '../lib/format';

type Filter = 'tutte' | 'expiring' | 'expired';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'tutte', label: 'Tutte' },
  { value: 'expiring', label: 'In scadenza' },
  { value: 'expired', label: 'Scadute' },
];

export function GaranzieScreen() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('tutte');
  const { activeId } = useMembers();
  const { data, loading } = useAsync(
    () => store.warranties({ status: filter === 'tutte' ? undefined : filter, member_id: activeId ?? undefined }),
    [filter, activeId],
  );

  return (
    <div className="screen">
      <div className="topbar">
        <div className="topbar__title">Garanzie</div>
        <button className="iconbtn" style={{ background: 'var(--accent)', color: 'var(--atext)', border: 'none' }} onClick={() => navigate('/nuovo?tab=garanzia')} aria-label="Aggiungi garanzia">
          <Plus size={20} />
        </button>
      </div>

      {/* 2 stat */}
      <div className="stats stats--2" style={{ marginBottom: 16 }}>
        <div className="card stat" style={{ background: 'var(--asoft)' }}>
          <div className="stat__num">{data?.summary.active ?? '—'}</div>
          <div className="stat__label">attive</div>
        </div>
        <div className="card stat" style={{ background: 'var(--asoft)' }}>
          <div className="stat__num">{data?.summary.expiring ?? '—'}</div>
          <div className="stat__label">in scadenza</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Chips options={FILTERS} active={filter} onChange={setFilter} />
      </div>

      {loading ? (
        <SkeletonList rows={3} />
      ) : data && data.items.length > 0 ? (
        <div className="list">
          {data.items.map((w) => {
            const near = w.status === 'expiring' || w.status === 'expired';
            return (
              <button key={w.id} className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }} onClick={() => navigate(`/garanzie/${w.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                  <IconChip>
                    <ShieldCheck size={20} />
                  </IconChip>
                  <div className="row__body">
                    <div className="row__title">{w.product}</div>
                    <div className="row__meta">
                      {w.seller} · {euro(w.amount_cents)}
                    </div>
                  </div>
                  <span className={`badge${near ? ' badge--accent' : ''}`}>{timeLeftBadge(w.days_left)}</span>
                </div>
                <ProgressBar value={w.progress} near={near} />
              </button>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<ShieldCheck size={48} />}
          title="Nessuna garanzia"
          message="Salva scontrini e fatture per non perdere mai una garanzia."
          cta={
            <button className="btn btn--accent" onClick={() => navigate('/nuovo?tab=garanzia')}>
              Aggiungi garanzia
            </button>
          }
        />
      )}
    </div>
  );
}
